// Google AI Studio (Gemini) Service per raccomandazioni artisti
import geminiLogger from './geminiLogger';
import SpotifyService from './spotify';
import { callGeminiViaProxy } from './geminiProxy';
import { 
  getBridgeConfig, 
  getFreeTextSearchConfig, 
  getSimilarArtistsConfig,
  getModelsForContext,
  GEMINI_MODELS_CONFIG 
} from '../config/aiProvider';

// Chiavi Gemini solo lato server (Supabase api_keys + Edge Function gemini-proxy).
const USE_PROXY = process.env.REACT_APP_USE_GEMINI_PROXY !== 'false';
const USE_GEMINI =
  process.env.REACT_APP_USE_GEMINI !== 'false' &&
  (process.env.REACT_APP_USE_GEMINI === 'true' || USE_PROXY);

// CONFIG: Scegli quale versione del prompt usare (1, 2, o 3)
const PROMPT_VERSION = 2; // Cambia qui per testare diverse versioni

// CONFIG: Abilita/disabilita salvataggio su database (per testing)
const ENABLE_DB_CACHE = false; // true = salva su Supabase

// CONFIG: Strategia a 2 fasi (2.5 Flash analizza → 2.0 Flash genera lista)
const USE_TWO_PHASE_STRATEGY = true; // true = analisi + generazione separate

// CONFIG: Genera e salva testo descrittivo artista (solo con strategia a 2 fasi)
const GENERATE_ARTIST_INFO = true; // true = genera testo discorsivo da salvare in DB

// Modelli Gemini ora vengono da aiProvider.js (configurazione unificata)
const GEMINI_MODELS = GEMINI_MODELS_CONFIG; // Mantenuto per retrocompatibilità, ma usa getModelsForContext()

const getApiKeyStats = () => ({
  mode: USE_PROXY ? 'proxy' : 'disabled',
  detail:
    'Chiavi Gemini solo su Supabase (tabella api_keys) e uso tramite Edge Function gemini-proxy.',
});

// Cache per evitare chiamate duplicate
const geminiCache = new Map();

/** Estrae testo dalla risposta Gemini (thinking + answer in parts) */
const textFromCandidateData = (data) => {
  const parts = data?.candidates?.[0]?.content?.parts;
  if (!parts?.length) return '';
  const withText = parts.map((p) => p?.text).filter(Boolean);
  if (!withText.length) return '';
  return withText.length === 1 ? withText[0] : withText[withText.length - 1];
};

const attemptProxyCall = async ({
  model,
  version = 'v1beta',
  prompt,
  systemInstruction = null,
  generationConfig,
  context,
  keyLabel = 'proxy',
  provider = 'gemini'
}) => {
  if (!USE_PROXY) {
    return { data: null, viaProxy: false };
  }

  const proxyResult = await callGeminiViaProxy({
    model,
    version,
    prompt,
    systemInstruction,
    generationConfig,
    context,
    keyLabel,
    provider
  });

  if (proxyResult && !proxyResult.error) {
    return { data: proxyResult, viaProxy: true };
  }

  return { data: null, viaProxy: true };
};

const parseGeminiJsonString = (rawText) => {
  let cleanedText = rawText.trim();
  const jsonMatch = cleanedText.match(/\{[\s\S]*\}/);
  if (jsonMatch) {
    cleanedText = jsonMatch[0];
  }
  return JSON.parse(cleanedText);
};

const processFreeTextProxyResponse = ({
  data,
  model,
  userRequest,
  prompt,
  language,
  startTime,
  promptLength
}) => {
  const responseTime = Date.now() - startTime;
  const finishReason = data.candidates?.[0]?.finishReason;
  const usageMetadata = data.usageMetadata;
  const text = data.candidates?.[0]?.content?.parts?.[0]?.text || '';

  if (!text) {
    geminiLogger.logApiError({
      context: 'free_text_search',
      artistName: userRequest.substring(0, 50),
      model: `${model.label} (Proxy)`,
      responseTime,
      errorMessage: 'Empty response'
    });
    return null;
  }

  let parsedData;
  try {
    parsedData = parseGeminiJsonString(text);
  } catch (error) {
    geminiLogger.logApiError({
      context: 'free_text_search',
      artistName: userRequest.substring(0, 50),
      model: `${model.label} (Proxy)`,
      responseTime,
      errorMessage: `JSON parse error: ${error.message}`,
      textSample: text.substring(0, 500)
    });
    return null;
  }

  if (parsedData.valid === false) {
    return {
      valid: false,
      message: parsedData.message || 'Non ho abbastanza informazioni per consigliarti musica.'
    };
  }

  if (!parsedData.valid || !Array.isArray(parsedData.artists)) {
    geminiLogger.logApiError({
      context: 'free_text_search',
      artistName: userRequest.substring(0, 50),
      model: `${model.label} (Proxy)`,
      responseTime,
      errorMessage: 'Invalid response format: missing or invalid artists array'
    });
    return null;
  }

  const items = parsedData.artists.map(item => ({
    name: item.name,
    match: (item.match || 0.8).toString(),
    genre: item.genre || '',
    origin: item.origin || '',
    url: `https://www.last.fm/music/${encodeURIComponent(item.name)}`
  }));

  geminiLogger.logApiSuccess({
    context: 'free_text_search',
    artistName: userRequest.substring(0, 50),
    model: `${model.label} (Proxy)`,
    responseTime,
    promptLength,
    outputLength: text.length,
    usageMetadata
  });

  return {
    valid: true,
    title: parsedData.title || (language === 'it' ? parsedData.title_it : parsedData.title_en) || '',
    analysis: parsedData.analysis || (language === 'it' ? parsedData.analysis_it : parsedData.analysis_en) || '',
    items,
    language
  };
};

/**
 * PROMPT FREE TEXT SEARCH - Ottimizzato per cache
 * Separa istruzioni statiche (cachate) da contenuto dinamico (non cachato)
 * I prompt restano IDENTICI, solo separati per ottimizzazione cache
 */
const getFreeTextSearchPrompt = (userRequest, searchType = 'artist', language = 'en') => {
  const targetLanguage = language === 'it' ? 'Italian' : 'English';
  
  // Solo anno (non cambia ogni minuto, mantiene cache valida più a lungo)
  const currentYear = new Date().getFullYear();

  // PARTE STATICA (CACHATA) - system_instruction
  // Contiene tutte le regole fisse che non cambiano tra richieste
  const systemInstruction = `Current date and time: ${currentYear} (Rome timezone)

IMPORTANT: 
- Provide the maximum number of coherent artists possible, ensuring a minimum of 20 and a maximum of 30 results
- Always aim for more results rather than fewer

Return ONLY this JSON (no thinking, no explanation):
{
  "valid": true,
  "title": "Title in ${targetLanguage} (max 10 words)",
  "analysis": "Brief analysis in ${targetLanguage} (max 120 words)",
  "artists": [
    {"name": "EXACT name", "genre": "Spotify genres (comma-separated, e.g. 'pop, adult contemporary, italian pop')", "origin": "Country"}
  ]
}
RULES:
- **CRITICAL: ONLY REAL ARTISTS THAT ACTUALLY EXIST - DO NOT INVENT ARTISTS - DO NOT CREATE FAKE ARTIST NAMES - VERIFY EACH ARTIST EXISTS BEFORE INCLUDING THEM**
- Max 30 artists
- **CRITICAL: Preserve EXACT spelling, including special characters, accents, and diacritics (e.g., "La Niña" NOT "La Nina", "José" NOT "Jose", "Björk" NOT "Bjork")**
- Use the official/correct spelling of artist names as they appear in music databases`;

  // PARTE DINAMICA (NON CACHATA) - user message
  // Solo la richiesta utente che cambia ogni volta
  const userMessage = `User wants: "${userRequest}"`;

  return {
    systemInstruction,
    userMessage
  };
};

/**
 * PROMPT FREE TEXT BRIDGE - Ottimizzato per cache
 * Separa istruzioni statiche (cachate) da contenuto dinamico (non cachato)
 * I prompt restano IDENTICI, solo separati per ottimizzazione cache
 */
const getFreeTextBridgePrompt = (userRequest, language = 'en') => {
  const isItalian = language === 'it';
  const targetLanguageLabel = isItalian ? 'Italian' : 'English';
  
  // PARTE STATICA (CACHATA) - system_instruction
  // Voce: Mr. Foundly in prima persona singolare (è lui che cerca)
  const systemInstruction = `You are Mr. Foundly's bridge message generator. The message is spoken by Mr. Foundly in first person (I, not "we" or "Foundly").

VALIDATION: If the prompt is NOT about music (e.g. "hi", "test", too short), respond ONLY with this JSON:
${isItalian 
  ? `{"valid": false, "message": "Ops! Mi dispiace, ma questa richiesta non riguarda la musica. Prova a chiedermi qualcosa su artisti, album, canzoni o generi musicali!"}`
  : `{"valid": false, "message": "Oops! Sorry, but this request isn't about music. Try asking me something about artists, albums, songs, or music genres!"}`}

If valid, write a 60-80 word message in first person as Mr. Foundly. Start with "Ciao, sono Mr. Foundly" (Italian) or "Hi, I'm Mr. Foundly" (English), then describe what you are looking for or analyzing for the user. Warm tone. I recommend artists (not songs/albums). Use paragraphs. Conclude saying the answer is coming. NO JSON, NO lists, NO emojis. Complete the FULL message.

Reply in ${targetLanguageLabel}.`;

  // PARTE DINAMICA (NON CACHATA) - user message
  // Solo la richiesta utente che cambia ogni volta
  const userMessage = `User prompt: "${userRequest.trim()}"`;

  return {
    systemInstruction,
    userMessage
  };
};

// Esponi cache e info sicurezza (nessuna chiave nel browser)
if (typeof window !== 'undefined') {
  window.geminiCache = geminiCache;
  window.geminiApiKeys = {
    stats: getApiKeyStats
  };

  window.clearGeminiCache = () => {
    const count = geminiCache.size;
    geminiCache.clear();
    console.log(`%c[Gemini] Cache cleared - ${count} entries removed`, 'background: #FF9800; color: white; padding: 4px 8px; border-radius: 3px; font-weight: bold;');
  };

  if (USE_GEMINI && USE_PROXY) {
    console.log('%c[Gemini] Proxy attivo — chiavi solo lato server (Supabase)', 'background: #4CAF50; color: white; padding: 8px 16px; border-radius: 4px; font-weight: bold; font-size: 14px;');
    if (USE_TWO_PHASE_STRATEGY && PROMPT_VERSION === 2) {
      console.log('%c🚀 STRATEGIA A 2 FASI ATTIVA', 'background: #9C27B0; color: white; padding: 4px 8px; border-radius: 3px; font-weight: bold;');
      console.log('');
    }
    console.log('%c🤖 Modelli disponibili:', 'color: #2196F3; font-weight: bold;');
    GEMINI_MODELS_CONFIG.forEach((m, i) => {
      console.log(`   ${i + 1}. ${m.label} (${m.limits}) - ${m.description}`);
    });
    console.log(`%c📝 Prompt Version: ${PROMPT_VERSION}`, 'color: #9C27B0; font-weight: bold;');
    console.log(`%c💾 DB Cache: ${ENABLE_DB_CACHE ? 'Enabled' : 'Disabled'}`, 'color: #FF9800; font-weight: bold;');
    console.log('%c💡 Comandi:', 'color: #00BCD4; font-weight: bold;');
    console.log('   window.clearGeminiCache() — pulisci cache');
    console.log('   window.geminiApiKeys.stats() — modalità chiavi (proxy)');
    console.log('');
  } else if (!USE_GEMINI) {
    console.log('[Gemini] Disabilitato (REACT_APP_USE_GEMINI=false)');
  } else {
    console.warn('[Gemini] Proxy disattivato: impossibile chiamare Gemini senza esporre chiavi. Imposta REACT_APP_USE_GEMINI_PROXY (default: attivo).');
  }
}

// Vecchia strategia two-phase rimossa dal sorgente.

/**
 * Ottiene artisti simili usando Gemini AI
 * @param {string} artistName - Nome dell'artista
 * @param {string} spotifyId - ID Spotify (opzionale, per evitare omonimi)
 * @param {Array<string>} genres - Generi Spotify (opzionale, per disambiguazione)
 * @returns {Promise<Array>} - Array di artisti simili con formato compatibile LastFM
 */
export const getSimilarArtistsFromGemini = async (artistName, spotifyId = null, genres = null) => {
  if (!USE_GEMINI || !USE_PROXY) {
    if (USE_GEMINI && !USE_PROXY) {
      console.warn('[Similar Artists] Richiede proxy Supabase e chiavi nel database.');
    }
    return null;
  }

  geminiLogger.logCache({
    hit: false,
    artistName,
    spotifyId,
    context: 'similar_artists'
  });

  const singlePhaseStartTime = Date.now();
  const prompt = `Find 50 artists similar to "${artistName}".

Return ONLY valid JSON:
{"artists":[{"name":"Artist Name","genre":"genre","origin":"Country"},...]}

RULES:
- Only REAL artists
- Max 50 artists
- All artists must be verifiable
- **CRITICAL: Preserve EXACT spelling, including special characters, accents, and diacritics (e.g., "La Niña" NOT "La Nina", "José" NOT "Jose", "Björk" NOT "Bjork")**
- Use the official/correct spelling of artist names as they appear in music databases`;

  const modelsForSimilarArtists = getModelsForContext('similarArtists');
  const similarCfg = getSimilarArtistsConfig();
  const provider = similarCfg.provider;

  if (modelsForSimilarArtists.length === 0) {
    console.warn('[Similar Artists] Nessun modello disponibile');
    return null;
  }

  for (let i = 0; i < modelsForSimilarArtists.length; i++) {
    const model = modelsForSimilarArtists[i];
    const callStartTime = Date.now();
    const maxTokens = model.name.includes('2.5') ? 16384 : 8192;

    geminiLogger.logApiCall({
      context: 'similar_artists',
      artistName,
      spotifyId,
      genres,
      model: `${model.label} (Proxy)`,
      modelName: model.name,
      apiVersion: model.version || 'v1beta',
      promptVersion: PROMPT_VERSION,
      attempt: i + 1,
      totalModels: modelsForSimilarArtists.length,
      apiKey: 'proxy'
    });

    const { data } = await attemptProxyCall({
      model: model.name,
      version: model.version || 'v1beta',
      prompt,
      generationConfig: {
        temperature: 0.8,
        topK: 50,
        topP: 0.96,
        maxOutputTokens: maxTokens
      },
      context: 'similar_artists',
      keyLabel: `similar_artists_${i}`,
      provider
    });

    if (!data) {
      if (i < modelsForSimilarArtists.length - 1) {
        geminiLogger.logModelFallback({
          context: 'similar_artists',
          artistName,
          fromModel: model.label,
          toModel: modelsForSimilarArtists[i + 1].label,
          reason: 'proxy_empty'
        });
      }
      continue;
    }

    const responseTime = Date.now() - callStartTime;
    const textContent = textFromCandidateData(data);
    const finishReason = data.candidates?.[0]?.finishReason;

    if (!textContent) {
      if (finishReason === 'MAX_TOKENS') {
        geminiLogger.logApiError({
          context: 'similar_artists',
          artistName,
          model: model.label,
          responseTime,
          errorMessage: 'MAX_TOKENS: thinking consumed all available tokens',
          finishReason,
          thoughtsTokenCount: data.usageMetadata?.thoughtsTokenCount,
          totalTokenCount: data.usageMetadata?.totalTokenCount,
          maxOutputTokens: maxTokens
        });
      } else {
        geminiLogger.logApiError({
          context: 'similar_artists',
          artistName,
          model: model.label,
          responseTime,
          errorMessage: `No text content (finishReason: ${finishReason || 'unknown'})`,
          finishReason
        });
      }
      continue;
    }

    let cleanedText = textContent.trim();
    cleanedText = cleanedText.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();

    let parsedData;
    try {
      parsedData = JSON.parse(cleanedText);
      geminiLogger.logJsonParsing({
        context: 'similar_artists',
        artistName,
        model: model.label,
        success: true,
        textLength: cleanedText.length
      });
    } catch (parseError) {
      try {
        const firstBrace = cleanedText.indexOf('{');
        const lastBrace = cleanedText.lastIndexOf('}');
        if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
          parsedData = JSON.parse(cleanedText.substring(firstBrace, lastBrace + 1));
          geminiLogger.logJsonParsing({
            context: 'similar_artists',
            artistName,
            model: model.label,
            success: true,
            jsonExtracted: true,
            method: 'brace_extraction'
          });
        } else {
          throw parseError;
        }
      } catch (secondError) {
        geminiLogger.logJsonParsing({
          context: 'similar_artists',
          artistName,
          model: model.label,
          success: false,
          error: secondError.message,
          textLength: cleanedText.length,
          finishReason
        });
        continue;
      }
    }

    if (!parsedData.artists || !Array.isArray(parsedData.artists)) {
      geminiLogger.logApiError({
        context: 'similar_artists',
        artistName,
        model: model.label,
        responseTime,
        errorMessage: 'Invalid response format: missing or invalid artists array',
        parsedData: JSON.stringify(parsedData).substring(0, 200)
      });
      continue;
    }

    const artists = parsedData.artists.map((artist) => ({
      name: artist.name,
      match: artist.match.toString(),
      url: `https://www.last.fm/music/${encodeURIComponent(artist.name)}`,
      image: []
    }));

    const totalTime = Date.now() - singlePhaseStartTime;

    geminiLogger.logApiSuccess({
      context: 'similar_artists',
      artistName,
      model: model.label,
      responseTime,
      totalTime,
      artistCount: artists.length,
      usageMetadata: data.usageMetadata,
      promptTokens: data.usageMetadata?.promptTokenCount,
      candidatesTokens: data.usageMetadata?.candidatesTokenCount,
      totalTokens: data.usageMetadata?.totalTokenCount,
      promptVersion: PROMPT_VERSION
    });

    geminiLogger.logPerformance({
      context: 'similar_artists',
      artistName,
      model: model.label,
      responseTime,
      totalTime,
      artistCount: artists.length,
      attempt: i + 1,
      promptVersion: PROMPT_VERSION
    });

    return artists;
  }

  return null;
};

/**
 * Resetta la cache di Gemini per un artista specifico
 * @param {string} artistName - Nome dell'artista
 */
export const resetGeminiCache = (artistName) => {
  const cacheKey = artistName.toLowerCase();
  if (geminiCache.has(cacheKey)) {
    geminiCache.delete(cacheKey);
  }
};

/**
 * Verifica se Gemini è abilitato e configurato
 * @returns {boolean}
 */
export const isGeminiEnabled = () => {
  return USE_GEMINI && USE_PROXY;
};

/**
 * Ottiene statistiche sulla cache di Gemini
 * @returns {object}
 */
export const getGeminiCacheStats = () => {
  return {
    size: geminiCache.size,
    artists: Array.from(geminiCache.keys()),
    enabled: isGeminiEnabled()
  };
};

/**
 * Verifica se il salvataggio su DB è abilitato
 * @returns {boolean}
 */
export const isDbCacheEnabled = () => {
  return ENABLE_DB_CACHE;
};

/**
 * PROMPT PER TRACCE SIMILI - Analisi approfondita con strategia a cerchi concentrici
 */
const getTrackSimilarityPrompt = (trackName, artistName, spotifyTrackId = null) => {
  const identityInfo = spotifyTrackId 
    ? `"${trackName}" by ${artistName} (Spotify Track ID: ${spotifyTrackId})`
    : `"${trackName}" by ${artistName}`;
  
  return `You're an expert music curator. Find 50 tracks similar to ${identityInfo}.

${spotifyTrackId ? `Use Spotify Track ID ${spotifyTrackId} to identify the EXACT track.` : ``}

CRITICAL RULES:
1. DO NOT include tracks by ${artistName} (the same artist). The purpose is to discover NEW music from DIFFERENT artists that sound similar.
2. MAXIMUM 3 TRACKS PER ARTIST: To ensure variety, limit each artist/band to maximum 3 tracks in the entire list of 50. This prevents repetitive recommendations from the same artist (e.g., don't include 20 Verve songs when searching Oasis tracks - max 3 Verve tracks).

STEP 1 - DEEP TRACK ANALYSIS (analyze this internally before recommending):

1. GENERRE E SOTTOGENERI:
   - Identify the main musical genre (Rock, Pop, Electronic, Jazz, Classical, etc.)
   - Identify the SPECIFIC subgenre (Indie Rock, Synth-Pop, Trap, Smooth Jazz, etc.)
   - This is the fundamental starting point.

2. STRUTTURA E ARRANGIAMENTO:
   - Form: How is the song structured (Intro, Verse, Pre-chorus, Chorus, Bridge, Outro)?
   - Instrumentation: Which instruments dominate (electric guitar, synthesizers, strings, acoustic/electronic drums)?
   - Density: Is the arrangement minimalist or rich and layered?

3. ELEMENTI ARMONICI E MELODICI:
   - Key and Mode: Is it in major (generally "happy") or minor (generally "melancholic")?
   - Harmonic Progression: Are harmonies simple and repetitive (classic pop/rock) or complex and changing (jazz/progressive)?
   - Melody: Is the vocal line wide and virtuosic or more linear and rhythmic?

4. RITMO E GROOVE:
   - Tempo (BPM): Is it slow (Ballad), medium, or fast (Dance/Punk)?
   - Time Signature: Is it 4/4 (most common) or unusual (3/4, 5/4, etc.)?
   - Rhythm Type: Is the rhythm straight and pulsating (Rock/Dance) or more syncopated and relaxed (R&B/Funk)?

5. TIMBRO E PRODUZIONE:
   - Sound Quality: Is the sound modern and crystalline or more lo-fi and "vintage"?
   - Reverb/Effects: Is there a lot of reverb, delay, or other effects creating a specific atmosphere?
   - Dynamics: Does the song have constant volume or large differences between verse and chorus?

6. ATMOSFERA, EMOZIONE E LIRICHE:
   - Mood: What's the general atmosphere (melancholic, energetic, dreamy, aggressive)?
   - Themes: What are the main themes in the lyrics (love, protest, introspection)?

7. ARTISTA E PERIODO STORICO/GEOGRAFICO:
   - Context: Who is the artist and when was the track released? This helps identify influences and contemporary/similar artists.

STEP 2 - RECOMMENDATION STRATEGY (50 tracks using concentric circles approach):

CIRCLE 1: IDENTITY (Top 10 most similar tracks)
   - Focus: Tracks that share simultaneously Genre, Atmosphere, Instrumentation, and similar Artist/Period.
   - Criteria: Same subgenre and very similar production sound.
   - Example: Same artist or artists heavily influenced by them.

CIRCLE 2: GENRE AND ATMOSPHERE (Next 20 tracks)
   - Focus: Tracks that share the Subgenre and main Mood, but may have small variations in arrangement or historical period.
   - Criteria: Similar energy, basic instrumentation (e.g., both very piano-driven).
   - Example: Contemporary artists in the same subgenre.

CIRCLE 3: SPECIFIC ELEMENTS (Next 10 tracks)
   - Focus: Tracks that share a distinctive technical or production element, even if genre is slightly different.
   - Criteria: Same distinctive harmonic progression, use of same type of synthesizer/vocal sound, or same Producer/Sound Engineer.
   - Example: Artists using identical electronic drum timbre, even if genres differ (e.g., Pop and Hip-Hop).

CIRCLE 4: INFLUENCE AND CONTROLLED CONTRAST (Last 10 tracks)
   - Focus: Tracks representing the original artist's influences or tracks from different genres sharing a common emotion or rhythm.
   - Criteria: Tracks that clearly inspired the original, or tracks offering contrast but maintaining a link (e.g., same tempo and melancholic mood, but one acoustic and one electronic).

This way, we move from almost identical similarity to more conceptual similarity, providing a list of 50 tracks that satisfy both closeness (Circles 1 and 2) and quantity and stimulating variety (Circles 3 and 4).

IMPORTANT:
- All tracks must be REAL and verifiable
- Order by similarity from highest to lowest
- Match scores: 0.95 (very similar) down to 0.55 (interesting tangential connection)
- Multiple tracks can share the SAME score if equally similar
- Format: Track Name - Artist Name

Return ONLY valid JSON (no markdown, no explanation):
{
  "tracks": [
    {"track": "Track Name", "artist": "Artist Name", "match": 0.89, "genre": "specific subgenre"},
    {"track": "Another Track", "artist": "Another Artist", "match": 0.85, "genre": "specific subgenre"},
    ...
  ]
}`;
};

/**
 * PROMPT FASE 1 TRACCE - Analisi profonda della traccia (per Gemini 2.5 Flash)
 */
const getTrackPhase1Prompt = (trackName, artistName, spotifyTrackId = null) => {
  const identityInfo = spotifyTrackId 
    ? `"${trackName}" by ${artistName} (Spotify Track ID: ${spotifyTrackId})`
    : `"${trackName}" by ${artistName}`;
  
  return `Analyze ${identityInfo} for music recommendations.${spotifyTrackId ? ` Use Spotify Track ID ${spotifyTrackId}.` : ''}

Provide concise profile:
- Origin (city/country), language, cultural scene
- Genre (broad + specific subgenre)
- Musical/production style, sound
- Lyrical themes, tone
- Scene position, influences
- Target audience, unique qualities
- Track structure, arrangement, tempo, mood

Return ONLY valid JSON:
{
  "track_name": "${trackName}",
  "artist_name": "${artistName}",
  "origin": "City, Country",
  "language": "Language/Dialect",
  "scene": "Description of music scene",
  "genre_broad": "Broad genre",
  "genre_specific": "Very specific subgenre",
  "musical_style": "Detailed description",
  "lyrical_themes": ["theme1", "theme2", "theme3"],
  "lyrical_style": "Description",
  "production_style": "Description",
  "sound_signature": "Description",
  "mood": "Overall mood/vibe",
  "energy_level": "high/medium/low",
  "tempo": "slow/medium/fast",
  "structure": "Track structure description",
  "unique_qualities": ["quality1", "quality2"],
  "key_influences": ["influence1", "influence2"],
  "target_audience": "Description of typical fan"
}`;
};

/**
 * PROMPT FASE 2 TRACCE - Generazione lista tracce (per Gemini 2.0 Flash)
 */
const getTrackPhase2Prompt = (trackName, artistName, analysis, generateInfo = false) => {
  const basePrompt = `You're an expert music curator. Find 50 tracks similar to "${trackName}" by ${artistName}.

CRITICAL RULES:
1. DO NOT include tracks by ${artistName} (the same artist). The purpose is to discover NEW music from DIFFERENT artists that sound similar.
2. MAXIMUM 3 TRACKS PER ARTIST: To ensure variety, limit each artist/band to maximum 3 tracks in the entire list of 50. This prevents repetitive recommendations from the same artist (e.g., don't include 20 Verve songs when searching Oasis tracks - max 3 Verve tracks).

TRACK PROFILE (from deep analysis):
${JSON.stringify(analysis, null, 2)}

Based on this detailed profile, find 50 similar tracks from DIFFERENT artists that match on MULTIPLE dimensions:

MATCHING PRIORITY:
1. Same local scene + same specific subgenre + similar themes = 0.85-0.95
2. Same region/language + same subgenre + similar mood = 0.75-0.84
3. Same genre + similar sound/production = 0.65-0.74
4. Genre match with some overlap = 0.55-0.64

IMPORTANT:
- Match on MULTIPLE dimensions (not just genre!)
- Consider: scene, language, lyrical themes, production style, mood, tempo
- Unknown tracks with strong profile match > Famous tracks with weak match
- Order by true similarity (not by fame)
- All tracks must be REAL and verifiable
- EXCLUDE tracks by ${artistName}`;

  if (generateInfo) {
    return basePrompt + `

ADDITIONALLY, create a BILINGUAL DISCURSIVE TEXT about "${trackName}" by ${artistName} (150-200 words per language):

Based on the analysis above, write TWO flowing descriptive paragraphs that capture the essence of this track - one in ITALIAN and one in ENGLISH.

EXCLUDE from the text:
- Track name (we already know what track we're talking about)
- Artist name (we already know who made it)
- Origin/country/city (geographic info)
- Language (linguistic details)  
- Key influences (other artists' names)
- Target audience description

INCLUDE in the text (make it flow naturally):
- Musical genre and specific subgenre
- Musical style and sound characteristics
- Production approach and instrumentation
- Lyrical themes and storytelling style
- Mood, atmosphere, and emotional tone
- Energy level and vibe
- Tempo and rhythm characteristics
- Scene position (underground/mainstream/experimental)
- What makes this track unique

Write in a DISCURSIVE, FLOWING style - like a music journalist describing the track to someone who's never heard of it. Be specific but accessible. Make it engaging.

BOTH texts should convey the SAME information but in natural, idiomatic language for each language.

Return ONLY valid JSON (no markdown, no explanation):
{
  "track_info_it": "Il tuo testo descrittivo fluente in italiano...",
  "track_info_en": "Your flowing descriptive text in English...",
  "tracks": [
    {"track": "Track Name", "artist": "Artist Name", "match": 0.89, "genre": "specific subgenre"},
    {"track": "Another Track", "artist": "Another Artist", "match": 0.85, "genre": "specific subgenre"},
    ...
  ]
}`;
  } else {
    return basePrompt + `

Return ONLY valid JSON (no markdown, no explanation):
{
  "tracks": [
    {"track": "Track Name", "artist": "Artist Name", "match": 0.89, "genre": "specific subgenre"},
    {"track": "Another Track", "artist": "Another Artist", "match": 0.85, "genre": "specific subgenre"},
    ...
  ]
}`;
  }
};

/**
 * STRATEGIA A 2 FASI PER TRACCE: Analisi + Generazione con descrizione
 * Fase 1: Gemini 2.5 Flash analizza la traccia
 * Fase 2: Gemini 2.0 Flash genera la lista usando l'analisi + descrizione bilingue
 */
async function getTwoPhaseTrackRecommendations(trackName, artistName, spotifyTrackId) {
  if (!USE_GEMINI || !USE_PROXY) return null;

  const totalStartTime = Date.now();
  const similarCfg = getSimilarArtistsConfig();
  const provider = similarCfg.provider;

  const phase1Prompt = getTrackPhase1Prompt(trackName, artistName, spotifyTrackId);
  const PHASE1_MODELS = [
    { name: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash', maxTokens: 16384 },
    { name: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash', maxTokens: 8192 }
  ];

  let phase1Data = null;
  let phase1Time = 0;
  let usedModelLabel = null;

  for (let modelIdx = 0; modelIdx < PHASE1_MODELS.length; modelIdx++) {
    const analysisModel = PHASE1_MODELS[modelIdx];
    geminiLogger.logApiCall({
      context: 'two_phase_track_analysis',
      phase: 1,
      artistName: `${trackName} by ${artistName}`,
      spotifyId: spotifyTrackId,
      model: analysisModel.label,
      promptLength: phase1Prompt.length
    });

    const t0 = Date.now();
    const { data } = await attemptProxyCall({
      model: analysisModel.name,
      version: 'v1beta',
      prompt: phase1Prompt,
      generationConfig: {
        temperature: 0.7,
        topK: 40,
        topP: 0.95,
        maxOutputTokens: analysisModel.maxTokens
      },
      context: 'two_phase_analysis',
      keyLabel: `track_p1_${modelIdx}`,
      provider
    });
    phase1Time = Date.now() - t0;

    if (data && !data.error) {
      phase1Data = data;
      usedModelLabel = analysisModel.label;
      break;
    }
  }

  if (!phase1Data) return null;

  const phase1Text = textFromCandidateData(phase1Data);
  if (!phase1Text) return null;

  let analysisClean = phase1Text.trim().replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
  const firstBrace = analysisClean.indexOf('{');
  const lastBrace = analysisClean.lastIndexOf('}');
  if (firstBrace !== -1 && lastBrace !== -1) {
    analysisClean = analysisClean.substring(firstBrace, lastBrace + 1);
  }

  let analysis;
  try {
    analysis = JSON.parse(analysisClean);
  } catch (e) {
    return null;
  }

  geminiLogger.logApiSuccess({
    context: 'two_phase_track_analysis',
    phase: 1,
    artistName: `${trackName} by ${artistName}`,
    model: usedModelLabel,
    responseTime: phase1Time,
    usageMetadata: phase1Data.usageMetadata,
    analysis: analysis
  });

  const phase2Prompt = getTrackPhase2Prompt(trackName, artistName, analysis, GENERATE_ARTIST_INFO);
  const phase2MaxTokens = GENERATE_ARTIST_INFO ? 16384 : 8192;
  const similarModels = getModelsForContext('similarArtists');
  const generationModel = similarModels.length > 0 ? similarModels[0] : GEMINI_MODELS_CONFIG[0];

  geminiLogger.logApiCall({
    context: 'two_phase_track_generation',
    phase: 2,
    artistName: `${trackName} by ${artistName}`,
    model: `${generationModel.label} (Proxy)`,
    promptLength: phase2Prompt.length,
    analysisUsed: analysis
  });

  const t2 = Date.now();
  const { data: phase2Data } = await attemptProxyCall({
    model: generationModel.name,
    version: generationModel.version || 'v1beta',
    prompt: phase2Prompt,
    generationConfig: {
      temperature: 0.8,
      topK: 50,
      topP: 0.96,
      maxOutputTokens: phase2MaxTokens
    },
    context: 'two_phase_generation',
    keyLabel: 'track_p2',
    provider
  });
  const phase2Time = Date.now() - t2;

  if (!phase2Data) return null;

  const phase2Text = textFromCandidateData(phase2Data);
  if (!phase2Text) return null;

  let tracksClean = phase2Text.trim().replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
  const fb2 = tracksClean.indexOf('{');
  const lb2 = tracksClean.lastIndexOf('}');
  if (fb2 !== -1 && lb2 !== -1) {
    tracksClean = tracksClean.substring(fb2, lb2 + 1);
  }

  let parsedData;
  try {
    parsedData = JSON.parse(tracksClean);
  } catch (e) {
    return null;
  }

  if (!parsedData.tracks || !Array.isArray(parsedData.tracks)) {
    return null;
  }

  const trackInfoIt = parsedData.track_info_it || null;
  const trackInfoEn = parsedData.track_info_en || null;
  const trackInfo = trackInfoIt && trackInfoEn ? { it: trackInfoIt, en: trackInfoEn } : null;

  const tracks = parsedData.tracks.map((track) => ({
    track: track.track || track.name,
    artist: track.artist,
    match: track.match.toString(),
    genre: track.genre || '',
    url: `https://www.last.fm/music/${encodeURIComponent(track.artist)}`
  }));

  const totalTime = Date.now() - totalStartTime;

  geminiLogger.logApiSuccess({
    context: 'two_phase_track_generation',
    phase: 2,
    artistName: `${trackName} by ${artistName}`,
    model: `${generationModel.label} (Proxy)`,
    responseTime: phase2Time,
    trackCount: tracks.length,
    usageMetadata: phase2Data.usageMetadata
  });

  geminiLogger.logPerformance({
    context: 'two_phase_tracks_complete',
    artistName: `${trackName} by ${artistName}`,
    phase1Time,
    phase2Time,
    totalTime,
    trackCount: tracks.length,
    strategy: 'two_phase_tracks',
    phase1Model: usedModelLabel,
    phase2Model: generationModel.label,
    totalTokens:
      (phase1Data.usageMetadata?.totalTokenCount || 0) +
      (phase2Data.usageMetadata?.totalTokenCount || 0)
  });

  return {
    tracks,
    trackInfo
  };
}

/**
 * Ottiene tracce simili usando Gemini AI (con stesso sistema fallback degli artisti)
 * @param {string} trackName - Nome della traccia
 * @param {string} artistName - Nome dell'artista
 * @param {string} spotifyTrackId - ID Spotify (opzionale, per evitare omonimi)
 * @returns {Promise<Array>} - Array di tracce simili
 */
export const getSimilarTracksFromGemini = async (trackName, artistName, spotifyTrackId = null) => {
  if (!USE_GEMINI || !USE_PROXY) {
    return null;
  }

  geminiLogger.logCache({
    hit: false,
    artistName: `${trackName} by ${artistName}`,
    spotifyId: spotifyTrackId,
    context: 'similar_tracks'
  });

  if (USE_TWO_PHASE_STRATEGY && PROMPT_VERSION === 2) {
    const result = await getTwoPhaseTrackRecommendations(trackName, artistName, spotifyTrackId);
    if (!result) return null;
    return result;
  }

  const similarCfg = getSimilarArtistsConfig();
  const provider = similarCfg.provider;
  const prompt = getTrackSimilarityPrompt(trackName, artistName, spotifyTrackId);
  const startTime = Date.now();

  for (let i = 0; i < GEMINI_MODELS.length; i++) {
    const model = GEMINI_MODELS[i];
    const callStartTime = Date.now();
    const maxTokens = model.name.includes('2.5') ? 16384 : 8192;

    geminiLogger.logApiCall({
      context: 'similar_tracks',
      artistName: `${trackName} by ${artistName}`,
      spotifyId: spotifyTrackId,
      model: `${model.label} (Proxy)`,
      modelName: model.name,
      apiVersion: model.version || 'v1beta',
      attempt: i + 1,
      totalModels: GEMINI_MODELS.length,
      apiKey: 'proxy'
    });

    const { data } = await attemptProxyCall({
      model: model.name,
      version: model.version || 'v1beta',
      prompt,
      generationConfig: {
        temperature: 0.8,
        topK: 50,
        topP: 0.96,
        maxOutputTokens: maxTokens
      },
      context: 'similar_tracks',
      keyLabel: `similar_tracks_${i}`,
      provider
    });

    if (!data) {
      if (i < GEMINI_MODELS.length - 1) {
        geminiLogger.logModelFallback({
          context: 'similar_tracks',
          artistName: `${trackName} by ${artistName}`,
          fromModel: model.label,
          toModel: GEMINI_MODELS[i + 1].label,
          reason: 'proxy_empty'
        });
      }
      continue;
    }

    const responseTime = Date.now() - callStartTime;
    const textContent = textFromCandidateData(data);
    const finishReason = data.candidates?.[0]?.finishReason;

    if (!textContent) {
      if (finishReason === 'MAX_TOKENS') {
        geminiLogger.logApiError({
          context: 'similar_tracks',
          artistName: `${trackName} by ${artistName}`,
          model: model.label,
          responseTime,
          errorMessage: 'MAX_TOKENS: thinking consumed all available tokens',
          finishReason,
          maxOutputTokens: maxTokens
        });
      }
      continue;
    }

    let cleanedText = textContent.trim().replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();

    let parsedData;
    try {
      parsedData = JSON.parse(cleanedText);
      geminiLogger.logJsonParsing({
        context: 'similar_tracks',
        artistName: `${trackName} by ${artistName}`,
        model: model.label,
        success: true,
        textLength: cleanedText.length
      });
    } catch (parseError) {
      try {
        const firstBrace = cleanedText.indexOf('{');
        const lastBrace = cleanedText.lastIndexOf('}');
        if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
          parsedData = JSON.parse(cleanedText.substring(firstBrace, lastBrace + 1));
          geminiLogger.logJsonParsing({
            context: 'similar_tracks',
            artistName: `${trackName} by ${artistName}`,
            model: model.label,
            success: true,
            jsonExtracted: true,
            method: 'brace_extraction'
          });
        } else {
          throw parseError;
        }
      } catch (secondError) {
        geminiLogger.logJsonParsing({
          context: 'similar_tracks',
          artistName: `${trackName} by ${artistName}`,
          model: model.label,
          success: false,
          error: secondError.message,
          finishReason
        });
        continue;
      }
    }

    if (!parsedData.tracks || !Array.isArray(parsedData.tracks)) {
      geminiLogger.logApiError({
        context: 'similar_tracks',
        artistName: `${trackName} by ${artistName}`,
        model: model.label,
        responseTime,
        errorMessage: 'Invalid response format: missing or invalid tracks array',
        parsedData: JSON.stringify(parsedData).substring(0, 200)
      });
      continue;
    }

    const tracks = parsedData.tracks.map((track) => ({
      track: track.track || track.name,
      artist: track.artist,
      match: track.match.toString(),
      genre: track.genre || '',
      url: `https://www.last.fm/music/${encodeURIComponent(track.artist)}`
    }));

    const totalTime = Date.now() - startTime;

    geminiLogger.logApiSuccess({
      context: 'similar_tracks',
      artistName: `${trackName} by ${artistName}`,
      model: model.label,
      responseTime,
      totalTime,
      artistCount: tracks.length,
      usageMetadata: data.usageMetadata,
      promptTokens: data.usageMetadata?.promptTokenCount,
      candidatesTokens: data.usageMetadata?.candidatesTokenCount,
      totalTokens: data.usageMetadata?.totalTokenCount
    });

    geminiLogger.logPerformance({
      context: 'similar_tracks',
      artistName: `${trackName} by ${artistName}`,
      model: model.label,
      responseTime,
      totalTime,
      artistCount: tracks.length,
      attempt: i + 1
    });

    return tracks;
  }

  return null;
};

/**
 * Estrae il testo dalla risposta Gemini (candidate).
 * Con modelli 2.5 Flash/Lite che usano thinking, parts può avere [thinking, risposta]:
 * usiamo l'ultima part con testo come messaggio effettivo; altrimenti parts[0].
 * Così il bridge funziona sia con 2.5 Flash Lite che con 2.5 Flash.
 */
const getTextFromGeminiCandidate = (data) => textFromCandidateData(data);

/**
 * Helper: Processa risposta bridge - controlla se è JSON di validazione o testo normale
 * @returns {string|{valid: false, message: string}} Testo normale o oggetto errore
 */
const processBridgeResponse = (text) => {
  if (!text || !text.trim()) return '';
  
  let trimmed = text.trim();
  
  // Cerca JSON all'inizio del testo (anche se seguito da altro testo)
  if (trimmed.startsWith('{')) {
    try {
      // Trova la fine del JSON (prima } che chiude l'oggetto)
      let jsonEnd = -1;
      let braceCount = 0;
      for (let i = 0; i < trimmed.length; i++) {
        if (trimmed[i] === '{') braceCount++;
        if (trimmed[i] === '}') {
          braceCount--;
          if (braceCount === 0) {
            jsonEnd = i;
            break;
          }
        }
      }
      
      if (jsonEnd > 0) {
        const jsonStr = trimmed.substring(0, jsonEnd + 1);
        const json = JSON.parse(jsonStr);
        
        // Se è un oggetto di validazione con valid: false, restituiscilo
        if (json.valid === false && json.message) {
          return json;
        }
        
        // Se è valid: true, rimuovi COMPLETAMENTE il JSON e prendi solo il testo dopo
        if (json.valid === true) {
          // Rimuovi il JSON e tutto lo spazio/newline dopo
          trimmed = trimmed.substring(jsonEnd + 1).trim();
          // Rimuovi anche eventuali newline o spazi multipli all'inizio
          trimmed = trimmed.replace(/^[\n\r\s]+/, '');
          // Se non c'è testo dopo, restituisci stringa vuota
          if (!trimmed) return '';
        }
      }
    } catch (e) {
      // Non è JSON valido, continua come testo normale
    }
  }
  
  // Rimuovi anche JSON che potrebbe essere nel mezzo o alla fine del testo
  // Cerca pattern come {"valid": true} o {"valid":false} ovunque nel testo
  // Rimuovi anche varianti con spazi diversi
  trimmed = trimmed.replace(/\{\s*"valid"\s*:\s*(true|false)\s*\}/g, '').trim();
  trimmed = trimmed.replace(/\{\s*"valid"\s*:\s*"(true|false)"\s*\}/g, '').trim();
  // Rimuovi anche eventuali code blocks JSON
  trimmed = trimmed.replace(/```json\s*\{\s*"valid"\s*:\s*(true|false)\s*\}\s*```/gi, '').trim();
  trimmed = trimmed.replace(/```\s*\{\s*"valid"\s*:\s*(true|false)\s*\}\s*```/gi, '').trim();
  
  // Testo normale - pulisci e restituisci
  let cleanedText = trimmed.replace(/\r\n/g, '\n');
  cleanedText = cleanedText.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n');
  return cleanedText;
};

/**
 * Messaggio bridge rapido (Gemini 2.0 Flash) per il loading screen
 * Intrattiene l'utente mentre la chiamata principale è in corso
 * Restituisce stringa (messaggio) o oggetto {valid: false, message: "..."} se invalido
 */
export const getFreeTextBridgeMessage = async (userRequest, language = 'en') => {
  if (!USE_GEMINI || !userRequest?.trim()) {
    return '';
  }
  if (!USE_PROXY) {
    console.warn('[Free Text Bridge] Richiede proxy Supabase (chiavi bridge nel DB).');
    return '';
  }

  const models = getModelsForContext('bridge');
  if (models.length === 0) {
    console.warn('[Free Text Bridge] Nessun modello disponibile');
    return '';
  }
  const model = models[0];
  const bridgeConfig = getBridgeConfig();
  const provider = bridgeConfig.provider;

  const promptData = getFreeTextBridgePrompt(userRequest, language);
  const promptLength =
    (promptData.systemInstruction?.length || 0) + (promptData.userMessage?.length || 0);

  const { data } = await attemptProxyCall({
    model: model.name,
    version: model.version || 'v1beta',
    systemInstruction: promptData.systemInstruction,
    prompt: promptData.userMessage,
    generationConfig: {
      temperature: 0.55,
      topK: 32,
      topP: 0.9,
      maxOutputTokens: 4096
    },
    context: 'free_text_bridge',
    keyLabel: 'bridge_proxy',
    provider
  });

  if (!data) {
    console.warn('[Free Text Bridge] Nessuna risposta dal proxy');
    return '';
  }

  const finishReason = data.candidates?.[0]?.finishReason;
  const text = getTextFromGeminiCandidate(data);

  if (finishReason === 'MAX_TOKENS') {
    console.warn(
      '[Gemini Bridge] ⚠️ Risposta troncata: MAX_TOKENS raggiunto. Testo ricevuto:',
      text.substring(0, 200)
    );
  }

  if (!text.trim()) {
    console.warn('[Gemini Bridge] ⚠️ Risposta vuota. finishReason:', finishReason);
    return '';
  }

  const processed = processBridgeResponse(text);

  if (typeof processed === 'object' && processed.valid === false) {
    return processed;
  }

  geminiLogger.logApiSuccess({
    context: 'free_text_bridge',
    model: `${model.label} (Proxy)`,
    responseTime: data._responseTime || null,
    promptLength,
    outputLength: typeof processed === 'string' ? processed.length : 0,
    finishReason,
    language
  });

  return processed;
};

/**
 * FOUNDLY 2.0 - Ricerca libera con prompt testuale/vocale
 * Analizza la richiesta dell'utente e genera raccomandazioni o risposta negativa
 */
export const getFreeTextSearchFromGemini = async (userRequest, searchType = 'artist', language = 'en') => {
  if (!USE_GEMINI) {
    console.log('[Free Text Search] AI disabled, returning null');
    return null;
  }
  if (!USE_PROXY) {
    console.warn('[Free Text Search] Richiede proxy Supabase (chiavi Gemini nel DB).');
    return null;
  }

  // Usa configurazione unificata da aiProvider.js
  const modelsToTry = getModelsForContext('freeTextSearch');
  if (modelsToTry.length === 0) {
    console.warn('[Free Text Search] Nessun modello disponibile');
    return null;
  }
  const freeTextConfig = getFreeTextSearchConfig();
  const provider = freeTextConfig.provider; // Recupera provider dalla configurazione

  const forcedSearchType = 'artist';
  const startTime = Date.now();
  const promptData = getFreeTextSearchPrompt(userRequest, forcedSearchType, language);
  const promptLength = (promptData.systemInstruction?.length || 0) + (promptData.userMessage?.length || 0);
  const generationConfigForModel = (model) => ({
    temperature: 0.2, 
    topK: 20, 
    topP: 0.85, 
    maxOutputTokens: model.name.includes('2.5') || model.name.includes('3') ? 16384 : 4096
  });

  for (let i = 0; i < modelsToTry.length; i++) {
    const model = modelsToTry[i];

    const genConfig = generationConfigForModel(model);
    console.log(`[Free Text Search] 🔧 Configurazione generazione:`, {
      temperature: genConfig.temperature,
      topK: genConfig.topK,
      topP: genConfig.topP,
      model: model.name,
      userRequest: userRequest.substring(0, 50) + '...'
    });
    
    const { data } = await attemptProxyCall({
      model: model.name,
      version: model.version || 'v1beta',
      systemInstruction: promptData.systemInstruction,
      prompt: promptData.userMessage, // Solo la parte dinamica
      generationConfig: genConfig,
      context: 'free_text_search',
      keyLabel: `${model.label}-proxy`,
      provider
    });

    if (!data) {
      console.warn(`[Free Text Search] Risposta vuota dal proxy per ${model.label}`);
      continue;
    }

    const result = processFreeTextProxyResponse({
      data,
      model,
      userRequest,
      prompt: promptData.systemInstruction + '\n\n' + promptData.userMessage, // Ricostruisci per logging
      language,
      startTime,
      promptLength
    });

    if (result) {
      return result;
    }
  }

  console.error('[Free Text Search] Nessuna risposta valida dal proxy Gemini');
  return null;
};
